import z from 'zod'

import WeightUnit from '@/enums/weightUnit'

const fosterFormVaccinationSchema = z.object({
  id: z.string().uuid(),
  vaccineId: z.string().uuid({ message: 'Please select a vaccine' }),
  fosterId: z.string().uuid(),
  name: z.string().nullable().optional(),
  dueDate: z.date().nullable().optional(),
  givenDate: z.date().nullable().optional(),
  longTermBoosterCadence: z.number().nullable().optional(),
  givenBy: z.string().nullable().optional(),
})

const fosterFormMedicationSchema = z
  .object({
    id: z.string().uuid(),
    fosterId: z.string().uuid(),
    name: z.string().min(3, { message: 'Name is required' }),
    dueDate: z.date().nullable().optional(),
    givenDate: z.date().nullable().optional(),
    givenBy: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    isRepeating: z.boolean().default(false),
    frequency: z.number().nullable().optional(),
  })
  .superRefine((m: any, ctx) => {
    if (!m) return
    const name = typeof m.name === 'string' ? m.name.trim().toLowerCase() : ''

    const isGenericPrevention = name.includes('prevention')

    if (m.givenDate && isGenericPrevention) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'Select the brand name of the prevention given',
      })
    }
  })

const fosterFormSchema = z.object({
  birthDate: z.date().nullable().optional(),
  breed: z.string().nullable().optional(),
  clearFecalTestDate: z.date().nullable().optional(),
  comboTestingCompletedDate: z.date().nullable().optional(),
  currentFood: z.string().nullable().optional(),
  deceasedDate: z.date().nullable().optional(),
  fecalFollowUpDate: z.date().nullable().optional(),
  felvPositive: z.boolean(),
  fivPositive: z.boolean(),
  fixedBeforeIntake: z.boolean(),
  fixedOnDate: z.date().nullable().optional(),
  gender: z.enum(['Male', 'Female']).nullable().optional(),
  heartwormPositive: z.boolean(),
  heartwormTestingCompletedDate: z.date().nullable().optional(),
  history: z.string().nullable().optional(),
  intakeDate: z.date().nullable().optional(),
  isAdopted: z.boolean(),
  isHospice: z.boolean(),
  microchipId: z.string().nullable().optional(),
  name: z.string().min(1),
  nextComboMedDue: z.date().nullable().optional(),
  nextFleaTickMedDue: z.date().nullable().optional(),
  nextHeartwormPreventionDue: z.date().nullable().optional(),
  notes: z.string().nullable().optional(),
  positiveFecalTestDate: z.date().nullable().optional(),
  privateNotes: z.string().nullable().optional(),
  readyForAdoption: z.boolean(),
  species: z.enum(['Dog', 'Cat']).nullable().optional(),
  training: z.string().nullable().optional(),
  userId: z.string().uuid(),
  vaccinations: z.array(fosterFormVaccinationSchema),
  medications: z.array(fosterFormMedicationSchema),
  weight: z
    .number({ coerce: true, invalid_type_error: 'Weight must be a number' })
    .min(1, { message: 'Weight must be greater than 0' })
    .nullable()
    .optional(),
  weightUnit: z.enum(Object.values(WeightUnit) as [`${WeightUnit}`]),
})

export type FosterForm = z.infer<typeof fosterFormSchema>

export default fosterFormSchema
